import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { initialsOf, useAccount } from '../auth/AccountContext';

interface Crumb {
  label: string;
  to?: string;
}

interface TopAppBarProps {
  title?: string;
  /** Optional leading back button; takes precedence over a breadcrumb. */
  onBack?: () => void;
  backTo?: string;
  /** Settings/Account/Plans style breadcrumb (e.g. Settings › Account). */
  breadcrumb?: Crumb[];
  /** Trailing action icons rendered on the right. */
  actions?: ReactNode;
}

/**
 * TopAppBar — pinned header with a strict 64px (h-16) height matching the
 * design specs: title on the left (with optional back arrow / breadcrumb),
 * trailing actions on the right.
 * Sits to the right of the 80px rail on md+ and spans full width on mobile.
 */
export default function TopAppBar({ title, onBack, backTo, breadcrumb, actions }: TopAppBarProps) {
  const visibleBreadcrumb = breadcrumb?.filter(
    (crumb, index) => !(title && index === breadcrumb.length - 1 && crumb.label === title),
  );

  return (
    <header className="fixed left-0 right-0 top-0 z-40 flex h-16 items-center justify-between gap-3 border-b border-outline-variant/30 bg-surface px-gutter py-2 md:left-[80px]">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {(onBack || backTo) && <BackButton onBack={onBack} backTo={backTo} />}
        <div className="min-w-0 flex-1">
          {visibleBreadcrumb && visibleBreadcrumb.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 font-label-sm text-label-sm text-on-surface-variant">
              {visibleBreadcrumb.map((c, i) => (
                <span key={i} className="flex items-center gap-2">
                  {c.to ? (
                    <Link to={c.to} className="transition-colors hover:text-primary">
                      {c.label}
                    </Link>
                  ) : (
                    <span>{c.label}</span>
                  )}
                  {i < visibleBreadcrumb.length - 1 && (
                    <Icon name="chevron_right" className="text-[14px] opacity-50" />
                  )}
                </span>
              ))}
            </div>
          )}
          {title && (
            <h1 className="truncate font-headline-md text-headline-md font-bold text-on-surface">
              {title}
            </h1>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">{actions}</div>
    </header>
  );
}

function BackButton({ onBack, backTo }: { onBack?: () => void; backTo?: string }) {
  const className =
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary';
  if (backTo) {
    return (
      <Link to={backTo} className={className} title="Back">
        <Icon name="arrow_back" />
      </Link>
    );
  }
  return (
    <button type="button" onClick={onBack} className={className} title="Back">
      <Icon name="arrow_back" />
    </button>
  );
}

/** Trailing action button used in most app bars. */
export function AppBarAction({
  icon,
  label,
  to,
  onClick,
}: {
  icon: string;
  label: string;
  to?: string;
  onClick?: () => void;
}) {
  const className =
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary';
  if (to) {
    return (
      <Link to={to} className={className} title={label}>
        <Icon name={icon} />
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} title={label}>
      <Icon name={icon} />
    </button>
  );
}

/**
 * AccountActionButton — compact profile avatar (picture/initials/icon)
 * constrained to exactly 40x40px to ensure the navbar height remains uniform.
 */
export function AccountActionButton() {
  const { user } = useAccount();

  if (user?.picture) {
    return (
      <Link
        to="/app/account"
        title="Account"
        aria-label="Account"
        className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-outline-variant transition-transform hover:scale-105"
      >
        <img
          src={user.picture}
          alt={user.name || 'User profile'}
          className="h-10 w-10 shrink-0 rounded-full object-cover"
        />
      </Link>
    );
  }

  if (user) {
    return (
      <Link
        to="/app/account"
        title="Account"
        aria-label="Account"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-outline-variant bg-surface-container-high font-label-md text-label-md font-semibold text-on-surface transition-colors hover:bg-surface-container-highest"
      >
        {initialsOf(user.name)}
      </Link>
    );
  }

  return (
    <Link
      to="/app/account"
      title="Account"
      aria-label="Account"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary"
    >
      <Icon name="account_circle" />
    </Link>
  );
}
